import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

const CancellationPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.user_id || userData.plan_status !== 'active_pro') {
      navigate('/dashboard');
      return;
    }
    setUser(userData);
  }, [navigate]);

  const handleCancel = async () => {
    setCancelling(true);
    setError('');

    try {
      const response = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Cancellation failed');
      }

      // Show success state
      setCancelled(true);
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      
    } catch (err) {
      setError(err.message);
      setCancelling(false);
    }
  };

  if (!user) return null;

  // Success state after cancellation
  if (cancelled) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: theme.mainPanelBg }}
      >
        <div className="max-w-xl w-full">
          <div 
            className="rounded-xl shadow-lg p-8 text-center"
            style={{ backgroundColor: theme.sidebarBg }}
          >
            <div className="mb-6">
              <Loader className="animate-spin mx-auto" size={64} style={{ color: theme.buttonBg }} />
            </div>
            
            <h1 
              className="text-2xl font-bold mb-4"
              style={{ color: theme.mainPanelText }}
            >
              Cancellation In Progress
            </h1>
            
            <div 
              className="p-6 rounded-lg mb-6"
              style={{ backgroundColor: theme.mainPanelBg }}
            >
              <p 
                className="text-lg mb-4"
                style={{ color: theme.mainPanelText }}
              >
                Your Pro subscription is being cancelled.
              </p>
              <p 
                className="text-sm opacity-75"
                style={{ color: theme.mainPanelText }}
              >
                You'll receive a confirmation email shortly. Your account will be reset to the free tier (50,000 tokens).
              </p>
            </div>
            
            <p 
              className="text-sm opacity-60"
              style={{ color: theme.mainPanelText }}
            >
              Redirecting to dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: theme.mainPanelBg }}
    >
      <div className="max-w-2xl w-full">
        
        {!showConfirmation ? (
          <div 
            className="rounded-xl shadow-lg p-8"
            style={{ backgroundColor: theme.sidebarBg }}
          >
            <h1 
              className="text-3xl font-bold mb-6"
              style={{ color: theme.mainPanelText }}
            >
              Cancel Pro Subscription
            </h1>

            <div className="mb-8">
              <h2 
                className="text-xl font-semibold mb-4"
                style={{ color: theme.mainPanelText }}
              >
                Current Plan Details
              </h2>
              <div 
                className="p-4 rounded-lg"
                style={{ backgroundColor: theme.mainPanelBg }}
              >
                <p style={{ color: theme.mainPanelText }}>
                  <strong>Plan:</strong> NoteStruct Pro
                </p>
                <p style={{ color: theme.mainPanelText }}>
                  <strong>Price:</strong> $9.99/month
                </p>
                <p style={{ color: theme.mainPanelText }}>
                  <strong>Current Tokens:</strong> Pro tier (500,000)
                </p>
              </div>
            </div>

            <div 
              className="p-6 rounded-lg border-l-4 mb-8"
              style={{ 
                backgroundColor: theme.mainPanelBg,
                borderLeftColor: '#f59e0b'
              }}
            >
              <div className="flex items-start space-x-3">
                <AlertCircle size={24} className="text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: theme.mainPanelText }}
                  >
                    What happens when you cancel:
                  </h3>
                  <ul 
                    className="space-y-2 text-sm"
                    style={{ color: theme.mainPanelText }}
                  >
                    <li>• Your subscription will end immediately</li>
                    <li>• No further charges will be made</li>
                    <li>• Your account will revert to Free tier (50,000 tokens)</li>
                    <li>• You'll lose access to Pro features</li>
                    <li>• You can reactivate Pro anytime</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 py-3 px-6 rounded-lg font-semibold border transition-all"
                style={{
                  borderColor: theme.inputBorder,
                  color: theme.mainPanelText,
                  backgroundColor: 'transparent'
                }}
              >
                Keep Pro
              </button>
              <button
                onClick={() => setShowConfirmation(true)}
                className="flex-1 py-3 px-6 rounded-lg font-semibold transition-all"
                style={{
                  backgroundColor: '#ef4444',
                  color: '#ffffff'
                }}
              >
                Continue to Cancel
              </button>
            </div>
          </div>
        ) : (
          <div 
            className="rounded-xl shadow-lg p-8"
            style={{ backgroundColor: theme.sidebarBg }}
          >
            <h1 
              className="text-2xl font-bold mb-6"
              style={{ color: theme.mainPanelText }}
            >
              Confirm Cancellation
            </h1>

            <p 
              className="text-lg mb-8"
              style={{ color: theme.mainPanelText }}
            >
              Are you absolutely sure you want to cancel your Pro subscription? 
              This action will take effect immediately.
            </p>

            {error && (
              <div 
                className="p-4 rounded-lg mb-6 bg-red-50 border-l-4 border-red-400"
              >
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={cancelling}
                className="flex-1 py-3 px-6 rounded-lg font-semibold border transition-all disabled:opacity-50"
                style={{
                  borderColor: theme.inputBorder,
                  color: theme.mainPanelText,
                  backgroundColor: 'transparent'
                }}
              >
                Go Back
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 py-3 px-6 rounded-lg font-semibold transition-all disabled:opacity-50"
                style={{
                  backgroundColor: '#ef4444',
                  color: '#ffffff'
                }}
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Subscription'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancellationPage;